import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import useQueryString from 'utils/useQueryString'
import useGetUserState from 'store/api/user/useGetUserState'
import { userApi } from 'store/api/user'
import { useIsTwoFaVisible } from 'utils/useIsTwoFaVisible'
import useRedirects from 'utils/useRedirects'
import { useSuccessfulRegistrationTracking } from 'utils/campaignTracking'
import useMaintenanceMode from './utils/useMaintenanceMode'
import { useDetectConfiguredDevice } from './utils/useDetectConfiguredDevice'
import { useGetNotificationsQuery } from 'store/api/notifications'
import { useGetProfilesQuery } from 'store/api/profiles'
import { useGetStatEndpointsQuery } from 'store/api/analytics'
import { organizationApi } from 'store/api/organization'
import { TrayRenderProps, useTrayPresenter } from 'ui'
import NavMenuMobile from './NavBar/NavMenu.mobile'
import OrganizationNavBarContent from '../Organization/OrganizationNavBar/OrganizationNavBarContent'
import NavBarContent from './NavBar/NavBarContent'
import { clearStateForImpersonation } from 'store/organization'
import { baseApi } from 'store/api'

export default function DashboardInitialization(): null {
  const userPk = useAppSelector(s => s.session.userPk)
  const dispatch = useAppDispatch()
  const { qs } = useQueryString()
  const { isOrganization, startPollingUser } = useGetUserState()
  const { data: userData } = userApi.endpoints.getUser.useQueryState('')
  const isConfirmEmailVisible = !!userData?.org?.PK && !userData?.email_status
  const isTwoFaVisible = useIsTwoFaVisible()
  const isMobileNavMenuOpen = useAppSelector(s => s.dashboard.isMobileNavMenuOpen)
  const prevOrgId = useRef<string | undefined>()

  //handle redirects from old paths
  useRedirects()
  useSuccessfulRegistrationTracking()
  useMaintenanceMode()
  useDetectConfiguredDevice()

  useGetNotificationsQuery('', { skip: !userPk })
  useGetProfilesQuery('')
  useGetStatEndpointsQuery('', {
    skip: !userPk,
    refetchOnMountOrArgChange: true,
  })

  const clearImpersonationState = useCallback(async () => {
    await dispatch(clearStateForImpersonation())
    await dispatch(baseApi.util.resetApiState())
    await dispatch(userApi.endpoints.getUser.initiate('', { forceRefetch: true }))
    await dispatch(organizationApi.endpoints.getOrganization.initiate('', { forceRefetch: true }))
  }, [dispatch])

  /** This effect invalidates the org data in the store and forces a refetch
   * when the orgId in the query string changes due to a sub-org being impersonated.
   */
  useLayoutEffect(() => {
    if (isOrganization) {
      if (prevOrgId.current && prevOrgId.current !== qs.orgId) {
        clearImpersonationState()
      }

      // this is important to avoid calling clearImpersonationState on the first render which results in duplicate requests.
      prevOrgId.current = qs.orgId || '-1'
    }
  }, [qs.orgId, isOrganization, clearImpersonationState])

  useEffect(() => {
    if (isConfirmEmailVisible && userData && !isTwoFaVisible) {
      startPollingUser(true)
    }

    return () => {
      startPollingUser(false)
    }
  }, [userData, isConfirmEmailVisible, startPollingUser, isTwoFaVisible])

  const navMobileTray = useMemo(
    () => ({
      id: 'NavMobileTray',
      renderTray: (props: TrayRenderProps) => (
        <NavMenuMobile {...props}>
          {isOrganization ? <OrganizationNavBarContent /> : <NavBarContent />}
        </NavMenuMobile>
      ),
      zIndexName: 'zIndex550',
    }),
    [isOrganization],
  )
  const { showTray: showMobileTray, hideTray: hideMobileTray } = useTrayPresenter(navMobileTray)

  useEffect(() => {
    isMobileNavMenuOpen ? showMobileTray() : hideMobileTray()
  }, [hideMobileTray, isMobileNavMenuOpen, showMobileTray])

  return null
}
